import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Text, Avatar, Box, ActionIcon, createStyles, rem } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconQuote } from '@tabler/icons-react';

interface Testimonial {
  quote: string;
  author: string;
  avatar: string;
  link: string;
}

interface TestimonialsCarouselProps {
  testimonials: Testimonial[];
}

const useStyles = createStyles((theme) => ({
  carouselWrapper: {
    position: 'relative',
    maxWidth: rem(800),
    margin: '0 auto',
    padding: `0 ${rem(48)}`,
    [theme.fn.smallerThan('sm')]: {
      padding: 0,
    },
  },

  viewport: {
    overflow: 'hidden',
    width: '100%',
    borderRadius: theme.radius.md,
    touchAction: 'pan-y', // Allow vertical scrolling on mobile, but intercept horizontal swipes
  },

  track: {
    display: 'flex',
    willChange: 'transform',
  },

  slide: {
    flex: '0 0 100%',
    width: '100%',
    padding: `${rem(12)} ${rem(12)}`,
    boxSizing: 'border-box',
  },

  testimonialCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: rem(220),
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(8px)',
    border: `1px solid ${theme.colors.blue[1]}`,
    borderRadius: theme.radius.lg,
    padding: rem(32),
    cursor: 'grab',
    textDecoration: 'none',
    userSelect: 'none',
    WebkitUserDrag: 'none',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',

    '&:active': {
      cursor: 'grabbing',
    },

    '&:hover': {
      boxShadow: '0 12px 30px rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-2px)',
    },

    [theme.fn.smallerThan('sm')]: {
      padding: rem(20),
      minHeight: rem(250),
    },
  },

  quoteHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing.xs,
  },

  quoteText: {
    fontSize: rem(18),
    lineHeight: 1.6,
    fontStyle: 'italic',
    color: theme.colors.gray[8],
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    marginBottom: rem(20),

    [theme.fn.smallerThan('sm')]: {
      fontSize: rem(15),
      lineHeight: 1.5,
    },
  },

  quoteIcon: {
    color: '#7b00b8',
    opacity: 0.15,
  },

  authorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    marginTop: 'auto',
    paddingTop: theme.spacing.md,
    borderTop: `1px solid ${theme.colors.gray[2]}`,
  },

  avatar: {
    border: `2px solid ${theme.colors.blue[1]}`,
    boxShadow: theme.shadows.xs,
  },

  authorInfo: {
    display: 'flex',
    flexDirection: 'column',
  },

  authorName: {
    fontWeight: 700,
    fontSize: rem(16),
    color: '#7b00b8',
    lineHeight: 1.2,
  },

  authorLocation: {
    fontSize: rem(13),
    color: theme.colors.gray[6],
    marginTop: rem(2),
  },

  arrowButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(4px)',
    border: `1px solid ${theme.colors.blue[1]}`,
    color: '#7b00b8',
    boxShadow: theme.shadows.sm,
    width: rem(40),
    height: rem(40),
    borderRadius: '50%',
    transition: 'background-color 0.2s ease, transform 0.2s ease, color 0.2s ease',

    '&:hover': {
      backgroundColor: '#7b00b8',
      color: theme.white,
      transform: 'translateY(-50%) scale(1.05)',
    },

    '&:active': {
      transform: 'translateY(-50%) scale(0.95)',
    },

    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },

  prevButton: {
    left: 0,
  },

  nextButton: {
    right: 0,
  },

  dotsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing.xs,
    marginTop: rem(16),
  },

  dot: {
    width: rem(10),
    height: rem(10),
    borderRadius: '50%',
    backgroundColor: theme.colors.gray[3],
    cursor: 'pointer',
    transition: 'background-color 0.3s ease, transform 0.3s ease',

    '&:hover': {
      backgroundColor: theme.colors.gray[5],
    },
  },

  activeDot: {
    backgroundColor: '#7b00b8',
    transform: 'scale(1.2)',
  },
}));

export default function TestimonialsCarousel({ testimonials }: TestimonialsCarouselProps) {
  const { classes } = useStyles();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);

  const startXRef = useRef(0);
  const currentXRef = useRef(0);
  const draggedDistanceRef = useRef(0);
  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < testimonials.length - 1 ? prev + 1 : 0));
  }, [testimonials.length]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : testimonials.length - 1));
  }, [testimonials.length]);

  const stopAutoPlay = useCallback(() => {
    if (autoPlayTimerRef.current) {
      clearInterval(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
    }
  }, []);

  const startAutoPlay = useCallback(() => {
    stopAutoPlay();
    autoPlayTimerRef.current = setInterval(() => {
      handleNext();
    }, 6000);
  }, [handleNext, stopAutoPlay]);

  useEffect(() => {
    startAutoPlay();
    return () => stopAutoPlay();
  }, [startAutoPlay, stopAutoPlay]);

  // Drag handlers
  const handleDragStart = (clientX: number) => {
    stopAutoPlay();
    setIsDragging(true);
    setIsTransitioning(false);
    startXRef.current = clientX;
    currentXRef.current = clientX;
    draggedDistanceRef.current = 0;
  };

  const handleDragMove = (clientX: number) => {
    if (!isDragging) return;
    currentXRef.current = clientX;
    const diffX = clientX - startXRef.current;
    draggedDistanceRef.current = Math.abs(diffX);
    setDragOffset(diffX);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    setIsTransitioning(true);
    
    const diffX = currentXRef.current - startXRef.current;
    const threshold = 60; // swipe threshold in pixels

    if (diffX > threshold) {
      // Swiped right (prev)
      setCurrentIndex((prev) => Math.max(0, prev - 1));
    } else if (diffX < -threshold) {
      // Swiped left (next)
      setCurrentIndex((prev) => Math.min(testimonials.length - 1, prev + 1));
    }

    setDragOffset(0);
    startAutoPlay();
  };

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    handleDragMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    handleDragStart(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    handleDragMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleDragEnd();
    }
  };

  const handleCardClick = (e: React.MouseEvent, link: string) => {
    // If user dragged more than 10 pixels, prevent navigation
    if (draggedDistanceRef.current > 10) {
      e.preventDefault();
      return;
    }
  };

  // Calculate track style
  const trackStyle: React.CSSProperties = {
    transform: `translateX(calc(-${currentIndex * 100}% + ${dragOffset}px))`,
    transition: isTransitioning ? 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)' : 'none',
  };

  return (
    <Box className={classes.carouselWrapper} onMouseEnter={stopAutoPlay} onMouseLeave={startAutoPlay}>
      {/* Left Chevron */}
      <ActionIcon
        className={`${classes.arrowButton} ${classes.prevButton}`}
        onClick={handlePrev}
        aria-label="Previous Testimonial"
      >
        <IconChevronLeft size="1.5rem" />
      </ActionIcon>

      {/* Viewport */}
      <div
        className={classes.viewport}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {/* Sliding Track */}
        <div className={classes.track} style={trackStyle}>
          {testimonials.map((testimonial, index) => (
            <div className={classes.slide} key={index}>
              <Card
                component="a"
                href={testimonial.link}
                onClick={(e) => handleCardClick(e, testimonial.link)}
                className={classes.testimonialCard}
                shadow="sm"
                withBorder
              >
                <div className={classes.quoteHeader}>
                  <IconQuote className={classes.quoteIcon} size={32} />
                  <Text className={classes.quoteText}>{testimonial.quote}</Text>
                </div>

                <div className={classes.authorRow}>
                  <Avatar
                    src={testimonial.avatar}
                    alt={testimonial.author}
                    size="lg"
                    radius="xl"
                    className={classes.avatar}
                  />
                  <div className={classes.authorInfo}>
                    <Text className={classes.authorName}>{testimonial.author.split(', ')[0]}</Text>
                    <Text className={classes.authorLocation}>{testimonial.author.split(', ')[1] || 'Comedy Seen User'}</Text>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Right Chevron */}
      <ActionIcon
        className={`${classes.arrowButton} ${classes.nextButton}`}
        onClick={handleNext}
        aria-label="Next Testimonial"
      >
        <IconChevronRight size="1.5rem" />
      </ActionIcon>

      {/* Dots Indicator */}
      <div className={classes.dotsContainer}>
        {testimonials.map((_, index) => (
          <div
            key={index}
            className={`${classes.dot} ${currentIndex === index ? classes.activeDot : ''}`}
            onClick={() => {
              setCurrentIndex(index);
              startAutoPlay();
            }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </Box>
  );
}
